import React, { useState } from 'react';
import { Box, IconButton, ListItemIcon, Menu, MenuItem, Stack, Typography } from '@mui/material';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import PushPinRoundedIcon from '@mui/icons-material/PushPinRounded';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import WorkOutlineRoundedIcon from '@mui/icons-material/WorkOutlineRounded';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import { useTranslation } from 'react-i18next';
import { brand } from '../../theme';

export const NOTIFICATION_KEY_MAP = {
    "You have logged in": "notifications.items.login",
    "You added a job": "notifications.items.addJob",
    "You added a seeking job": "notifications.items.addSeekingJob",
    "You edited a job": "notifications.items.editJob",
    "You edited a seeking job": "notifications.items.editSeekingJob",
    "You deleted a job": "notifications.items.deleteJob",
    "You deleted a seeking job": "notifications.items.deleteSeekingJob",
    "You added a favourite": "notifications.items.addFavourite",
    "You removed a favourite": "notifications.items.deleteFavourite",
    "You removed all favourites": "notifications.items.deleteAllFavourite",
    "You removed all posts": "notifications.items.deleteAllPosts",
    "You removed all seeking posts": "notifications.items.deleteAllSeekingPosts",
    "You sent an email to a candidate": "notifications.items.sendCandidateEmail",
    "You received an email from a candidate": "notifications.items.receiptCandidateEmail",
    // Names stored for the notification preferences (user_notifications table).
    "Login": "notifications.items.login",
    "Add Job": "notifications.items.addJob",
    "Add Seeking Job": "notifications.items.addSeekingJob",
    "Edit Job": "notifications.items.editJob",
    "Edit Seeking Job": "notifications.items.editSeekingJob",
    "Delete Job": "notifications.items.deleteJob",
    "Delete Seeking Job": "notifications.items.deleteSeekingJob",
    "Add Favourite": "notifications.items.addFavourite",
    "Delete Favourite": "notifications.items.deleteFavourite",
    "Delete All Favourite": "notifications.items.deleteAllFavourite",
    "Delete All Posts": "notifications.items.deleteAllPosts",
    "Delete All Seeking Posts": "notifications.items.deleteAllSeekingPosts",
    "Send Candidate Email": "notifications.items.sendCandidateEmail",
    "Receipt Candidate Email": "notifications.items.receiptCandidateEmail"
};

const iconFor = (type = '') => {
    if (type.includes('LOGIN')) return <LoginRoundedIcon fontSize="small" />;
    if (type.includes('FAVOURITE')) return <FavoriteBorderRoundedIcon fontSize="small" />;
    if (type.includes('EMAIL')) return <MailOutlineRoundedIcon fontSize="small" />;
    if (type.includes('JOB') || type.includes('POSTS')) return <WorkOutlineRoundedIcon fontSize="small" />;
    return <NotificationsNoneRoundedIcon fontSize="small" />;
};

export default function NotificationElement({ notification, handleDeleteSingleNotificationClicked, handleUpsertMarkOnTopClicked }) {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState(null);
    const key = NOTIFICATION_KEY_MAP[notification.notificationName];
    const name = key ? t(key, notification.notificationName) : notification.notificationName;
    const pinned = Boolean(notification.markOnTop);

    return (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 2, py: 1.25, '&:hover': { backgroundColor: '#F8FAFC' } }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '10px', display: 'grid', placeItems: 'center', flexShrink: 0, color: 'primary.main', backgroundColor: '#EEF2FF' }}>
                {iconFor(notification.notificationType)}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{name}</Typography>
                <Typography variant="caption" color="text.secondary">{notification.notificationData}</Typography>
            </Box>
            {pinned && <PushPinRoundedIcon fontSize="small" sx={{ color: brand.primary }} aria-label={t('notifications.markOnTop')} />}
            <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} aria-label={t('common.more')}>
                <MoreVertRoundedIcon fontSize="small" />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem onClick={() => { setAnchorEl(null); handleUpsertMarkOnTopClicked(notification.notificationId, pinned); }}>
                    <ListItemIcon>{pinned ? <PushPinOutlinedIcon fontSize="small" /> : <PushPinRoundedIcon fontSize="small" />}</ListItemIcon>
                    {pinned ? t('notifications.unmarkOnTop') : t('notifications.markOnTop')}
                </MenuItem>
                <MenuItem onClick={() => { setAnchorEl(null); handleDeleteSingleNotificationClicked(notification.notificationId); }} sx={{ color: 'error.main' }}>
                    <ListItemIcon><DeleteOutlineRoundedIcon fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
                    {t('common.delete')}
                </MenuItem>
            </Menu>
        </Stack>
    );
}
